import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { Camera } from '@liveauctioneers/hammer-ui-core/icons';
import { useEffect, useRef, useState } from 'react';

import { useImageSearch } from '@/hooks/useImageSearch';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useTrackSearchByImageClickedAnalytics } from '@/utils/amplitude/analytics/searchByImage/trackSearchByImageClicked.analytics';
import { useSearchByImageExperiment } from '@/utils/amplitude/experiments/hooks/useSearchByImage.experiment';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { SearchByImageBottomSheet } from './components/SearchByImageBottomSheet';
import { SearchByImagePopover } from './components/SearchByImagePopover';

/**
 * Camera entry point for Search by Image, rendered inside the right of the header
 * search input (as its endAdornment). The hook mounts across all arms so Amplitude
 * exposure fires; only treatment-a renders the camera. control and treatment-b render
 * nothing (treatment-b's distinct design ships in a follow-up).
 */
export const SearchByImageEntryPoint = () => {
    const { hasTreatmentA } = useSearchByImageExperiment();
    const isMobile = useIsMobileLayout();
    const formatMessage = useAppFormatMessage();
    const trackSearchByImageClicked = useTrackSearchByImageClickedAnalytics();
    const { clearError, status } = useImageSearch();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // The mobile bottom sheet portals out of this container and manages its own
        // dismissal (overlay tap / drag / Escape), so the outside-click + Escape
        // handling below is only for the desktop anchored popover.
        if (!isOpen || isMobile) {
            return;
        }
        const handleDocumentClick = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };
        document.addEventListener('click', handleDocumentClick);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('click', handleDocumentClick);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, isMobile]);

    if (!hasTreatmentA) {
        return null;
    }

    return (
        <div
            className="relative flex items-center"
            ref={containerRef}
        >
            <IconButton
                aria-expanded={isOpen}
                aria-haspopup={isMobile ? 'dialog' : 'true'}
                aria-label={String(formatMessage(['visualSearch', 'searchByImage']))}
                hideTooltip
                icon={Camera}
                onClick={() => {
                    if (!isOpen) {
                        // Clear a stale error so the surface reopens on a clean dropzone. This drops
                        // only the error (not the results), so reopening the camera over a results
                        // page keeps those results and never redirects home.
                        if (status === 'error') {
                            clearError();
                        }
                        trackSearchByImageClicked();
                    }
                    setIsOpen((open) => !open);
                }}
                size="sm"
                style="neutral"
                type="button"
                variant="transparent"
            />
            {isMobile ? (
                <SearchByImageBottomSheet
                    onClose={() => {
                        setIsOpen(false);
                    }}
                    open={isOpen}
                />
            ) : (
                isOpen && (
                    <SearchByImagePopover
                        onClose={() => {
                            setIsOpen(false);
                        }}
                    />
                )
            )}
        </div>
    );
};
